import React, { useMemo, useState, useEffect } from "react";
import { useGlobalContext } from "../Config";
import * as XLSX from "xlsx";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Card } from "primereact/card";

import { formatDateCL } from "../utilities/DateUtil";

const ConsumoMedidor = () => {
  const { baseUrl } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);

  const [empresaId, setEmpresaId] = useState(null);

  const [anio, setAnio] = useState(null); // 2024..2026
  const [mes, setMes] = useState(null); // 1..12

  // raw rows desde backend (boleta-medidor)
  const [rawItems, setRawItems] = useState([]);
  const [lastQuery, setLastQuery] = useState(null);

  const empresasOptions = [
    { label: "ALTAMIRA", value: 1 },
    { label: "LA SIEMBRA", value: 2 },
  ];

  const mesesOptions = [
    { label: "Enero", value: 1 },
    { label: "Febrero", value: 2 },
    { label: "Marzo", value: 3 },
    { label: "Abril", value: 4 },
    { label: "Mayo", value: 5 },
    { label: "Junio", value: 6 },
    { label: "Julio", value: 7 },
    { label: "Agosto", value: 8 },
    { label: "Septiembre", value: 9 },
    { label: "Octubre", value: 10 },
    { label: "Noviembre", value: 11 },
    { label: "Diciembre", value: 12 },
  ];

  const aniosOptions = [2024, 2025, 2026].map((y) => ({
    label: String(y),
    value: y,
  }));

  const buildPeriodo = () => {
    if (!anio || !mes) return "";
    return `${anio}-${String(mes).padStart(2, "0")}`;
  };

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const validar = () => {
    if (!empresaId) {
      alert("Debe seleccionar empresa.");
      return false;
    }
    if (!anio || !mes) {
      alert("Debe seleccionar año y mes.");
      return false;
    }
    return true;
  };

  const buscar = async () => {
    if (!validar()) return;

    const periodo = buildPeriodo();

    setIsLoading(true);
    try {
      const url =
        `${baseUrl}consumoMedidor` +
        `?empresaId=${encodeURIComponent(empresaId)}` +
        `&periodo=${encodeURIComponent(periodo)}`;

      const data = await fetchJson(url, { method: "GET" });

      const list = Array.isArray(data?.items) ? data.items : [];
      setRawItems(list);
      setLastQuery({ empresaId, periodo });
    } catch (e) {
      console.error(e);
      alert("Error consultando consumo medidor: " + (e?.message || e));
      setRawItems([]);
      setLastQuery(null);
    } finally {
      setIsLoading(false);
    }
  };

  const limpiar = () => {
    setEmpresaId(null);
    setAnio(null);
    setMes(null);
    setRawItems([]);
    setLastQuery(null);
  };

  const fmtMoneyCLP = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "-";
    return n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  };

  const fechaBody = (field) => (row) => formatDateCL(row?.[field]) || "-";

  const exportarExcel = () => {
    if (!boletas.length) {
      alert("No hay datos para exportar.");
      return;
    }

    const rows = boletas.map((row) => ({
      Proveedor: row?.proveedor ?? "",
      NumeroBoleta: row?.numeroBoleta ?? "",
      FechaPago: formatDateCL(row?.fechaPago ?? row?.fechaEmision) || "",
      FechaCaptura: formatDateCL(row?.fechaCaptura) || "",
      Cliente: row?.cliente ?? "",
      PeriodoDesde: formatDateCL(row?.periodoDesde) || "",
      PeriodoHasta: formatDateCL(row?.periodoHasta) || "",
      MontoTotal: Number(row?.montoTotal ?? 0),
      RecaudacionRemarcadores: Number(row?.recaudacionTeoricaTotal ?? 0),
      Diferencia: Number(diferenciaOf(row) ?? 0),
      CantidadMedidores: Array.isArray(row?.medidores)
        ? row.medidores.length
        : 0,
      Medidores: Array.isArray(row?.medidores)
        ? row.medidores
            .map(
              (m) =>
                `${m?.codigo ?? m?.medidorId ?? ""}${
                  m?.qrCode ? ` (QR:${m.qrCode})` : ""
                }`,
            )
            .join(", ")
        : "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = [
      { wch: 20 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 16 },
      { wch: 24 },
      { wch: 16 },
      { wch: 18 },
      { wch: 50 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ConsumoMedidor");

    const periodo = lastQuery?.periodo ?? "sin-periodo";
    XLSX.writeFile(wb, `consumo_medidor_${periodo}.xlsx`);
  };

  const boletaKeyOf = (r) => {
    const prov = String(r?.proveedor ?? "");
    const num = String(r?.numeroBoleta ?? "");
    const fp = String(r?.fechaPago ?? "");
    const hasta = String(r?.periodoHasta ?? "");
    const cli = String(r?.cliente ?? "");
    // suficiente para que no colisione en la práctica
    return `${prov}||${num}||${fp}||${hasta}||${cli}`;
  };

  const boletas = useMemo(() => {
    const map = new Map();

    for (const r of rawItems || []) {
      const key = boletaKeyOf(r);
      if (!key) continue;

      if (!map.has(key)) {
        map.set(key, {
          // campos boleta
          proveedor: r?.proveedor ?? "-",
          numeroBoleta: r?.numeroBoleta ?? "-",
          fechaPago: r?.fechaPago ?? r?.fechaEmision ?? null,
          fechaCaptura: r?.fechaCaptura ?? null,
          cliente: r?.cliente ?? "-",
          periodoDesde: r?.periodoDesde ?? null,
          periodoHasta: r?.periodoHasta ?? null,
          montoTotal: Number(r?.montoTotal ?? 0) || 0,

          // medidores asociados
          medidores: [],

          // recaudación total de remarcadores (suma por medidor)
          recaudacionTeoricaTotal: 0,
        });
      }

      const b = map.get(key);

      const mid = r?.medidorId != null ? Number(r.medidorId) : null;
      if (mid) {
        // evitar duplicar medidores si por alguna razón vienen repetidos
        const exists = b.medidores.some((x) => Number(x.medidorId) === mid);
        if (!exists) {
          b.medidores.push({
            medidorId: mid,
            codigo: r?.codigo ?? null,
            qrCode: r?.qrCode ?? null,
            tipo: r?.tipo ?? null,
            unidad: r?.unidad ?? null,
            activo: r?.activo ?? null,
            recaudacionTeoricaRemarcadores:
              Number(r?.recaudacionTeoricaRemarcadores ?? 0) || 0,
          });

          b.recaudacionTeoricaTotal +=
            Number(r?.recaudacionTeoricaRemarcadores ?? 0) || 0;
        }
      }
    }

    // salida ordenada (por fechaHasta desc si viene)
    const out = Array.from(map.values());
    out.sort((a, b) => {
      const da = String(a?.periodoHasta ?? "");
      const db = String(b?.periodoHasta ?? "");
      if (da === db) return 0;
      return da < db ? 1 : -1;
    });

    return out;
  }, [rawItems]);

  const recaudacionBoletaOf = (row) =>
    Number(row?.recaudacionTeoricaTotal ?? 0);

  const montoBoletaOf = (row) => Number(row?.montoTotal ?? 0);

  const diferenciaOf = (row) => recaudacionBoletaOf(row) - montoBoletaOf(row);

  const diffSeverity = (diff) => {
    const d = Number(diff ?? 0);
    if (Math.abs(d) < 1e-9) return "success";
    return d > 0 ? "success" : "danger";
  };

  const diffLabel = (diff) => {
    const d = Number(diff ?? 0);
    if (Math.abs(d) < 1e-9) return "CUADRA";
    return d > 0 ? "POSITIVO" : "NEGATIVO";
  };

  const recaudacionBody = (row) => `$ ${fmtMoneyCLP(recaudacionBoletaOf(row))}`;
  const montoTotalBoletaBody = (row) => `$ ${fmtMoneyCLP(montoBoletaOf(row))}`;

  const diferenciaBody = (row) => {
    const diff = diferenciaOf(row);
    const sev = diffSeverity(diff);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Tag severity={sev} value={diffLabel(diff)} />
        <span style={{ whiteSpace: "nowrap" }}>$ {fmtMoneyCLP(diff)}</span>
      </div>
    );
  };

  // Medidores asociados: tags
  const medidoresBody = (row) => {
    const meds = Array.isArray(row?.medidores) ? row.medidores : [];
    if (meds.length === 0) return "-";

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {meds.map((m) => (
          <Tag
            key={m.medidorId} // el key puede seguir siendo el id
            value={`${m.codigo ?? m.medidorId}${
              m.qrCode ? ` (QR:${m.qrCode})` : ""
            }`}
            severity={m.activo ? "success" : "warning"}
          />
        ))}
      </div>
    );
  };

  const totales = useMemo(() => {
    const totalBoletas = (boletas || []).reduce(
      (acc, row) => acc + montoBoletaOf(row),
      0,
    );
    const totalRecaudacion = (boletas || []).reduce(
      (acc, row) => acc + recaudacionBoletaOf(row),
      0,
    );
    const totalDiferencia = totalRecaudacion - totalBoletas;

    const sev = diffSeverity(totalDiferencia);
    const label = diffLabel(totalDiferencia);

    return {
      totalBoletas,
      totalRecaudacion,
      totalDiferencia,
      sev,
      label,
    };
  }, [boletas]);

  const cardStyleBySeverity = (sev) => {
    if (sev === "success")
      return { borderLeft: "6px solid #22c55e", background: "#f0fdf4" };
    if (sev === "warning")
      return { borderLeft: "6px solid #f59e0b", background: "#fffbeb" };
    return { borderLeft: "6px solid #ef4444", background: "#fef2f2" };
  };

  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    if (y >= 2024 && y <= 2026) {
      setAnio(y);
      setMes(m);
    }
  }, []);

  const tablaProps = {
    responsiveLayout: "scroll",
    className: "p-datatable-sm",
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 20],
    stripedRows: true,
    showGridlines: true,
    sortMode: "multiple",
  };

  const headerCard = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: "#495057" }}>Consumo Medidor</h4>
          <small style={{ color: "#6c757d" }}>
            1 fila por boleta. El periodo se determina por la fecha “hasta”.
          </small>
        </div>
      </div>
    ),
    [],
  );

  return (
    <div className="col-12 md:col-12">
      <ConfirmDialog />

      <div
        style={{
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
        }}
      >
        {headerCard}
      </div>

      {/* filtros */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-4">
            <label style={{ fontWeight: "bold" }}>Empresa</label>
            <Dropdown
              value={empresaId}
              options={empresasOptions}
              onChange={(e) => setEmpresaId(e.value)}
              placeholder="Seleccione empresa"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-4">
            <label style={{ fontWeight: "bold" }}>Periodo</label>
            <div style={{ display: "flex", gap: 10 }}>
              <Dropdown
                value={mes}
                options={mesesOptions}
                onChange={(e) => setMes(e.value)}
                placeholder="Mes"
                style={{ width: "60%" }}
                showClear
                disabled={isLoading}
              />
              <Dropdown
                value={anio}
                options={aniosOptions}
                onChange={(e) => setAnio(e.value)}
                placeholder="Año"
                style={{ width: "40%" }}
                showClear
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="col-12 md:col-4">
            <label style={{ fontWeight: "bold", opacity: 0 }}>Acciones</label>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                height: "42px",
                marginTop: 4,
              }}
            >
              <Button
                label="Buscar"
                icon="pi pi-search"
                onClick={buscar}
                disabled={isLoading}
              />
              <Button
                label="Limpiar"
                icon="pi pi-times"
                className="p-button-text"
                onClick={limpiar}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <ProgressSpinner />
        </div>
      )}

      {/* CARDS RESUMEN */}
      {boletas.length > 0 && (
        <div className="grid" style={{ marginBottom: 16 }}>
          <div className="col-12 md:col-4">
            <Card
              style={{
                borderRadius: 10,
                boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ color: "#6c757d", fontWeight: 700 }}>
                Monto total boletas
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 6 }}>
                $ {fmtMoneyCLP(totales.totalBoletas)}
              </div>
              <div style={{ color: "#6c757d", marginTop: 6 }}>
                Suma del monto total
              </div>
            </Card>
          </div>

          <div className="col-12 md:col-4">
            <Card
              style={{
                borderRadius: 10,
                boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ color: "#6c757d", fontWeight: 700 }}>
                Recaudación Remarcadores
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 6 }}>
                $ {fmtMoneyCLP(totales.totalRecaudacion)}
              </div>
              <div style={{ color: "#6c757d", marginTop: 6 }}>
                Suma recaudación (remarcadores) de los medidores en cada boleta
              </div>
            </Card>
          </div>

          <div className="col-12 md:col-4">
            <Card
              style={{
                borderRadius: 10,
                boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
                ...cardStyleBySeverity(totales.sev),
              }}
            >
              <div style={{ color: "#6c757d", fontWeight: 700 }}>
                Diferencia (recaudación - boletas)
              </div>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  marginTop: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span>$ {fmtMoneyCLP(totales.totalDiferencia)}</span>
                <Tag severity={totales.sev} value={totales.label} />
              </div>
              <div style={{ color: "#6c757d", marginTop: 6 }}>
                Positivo = recaudación mayor al monto de boletas
              </div>
            </Card>
          </div>
        </div>
      )}

      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            marginBottom: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Resultado:</strong>{" "}
            {lastQuery
              ? `Empresa ${lastQuery.empresaId} - Periodo ${lastQuery.periodo}`
              : "Sin consulta"}
            {"  "}
            <span style={{ color: "#6c757d" }}>({boletas.length} boletas)</span>
          </div>

          <Button
            label="Descargar Excel"
            icon="pi pi-file-excel"
            className="p-button-success p-button-sm"
            onClick={exportarExcel}
            disabled={!boletas.length || isLoading}
          />
        </div>

        <DataTable
          value={boletas}
          dataKey={(row) =>
            `${row?.proveedor ?? ""}||${row?.numeroBoleta ?? ""}||${
              row?.periodoHasta ?? ""
            }`
          }
          {...tablaProps}
        >
          <Column field="proveedor" header="Proveedor" sortable />
          <Column field="numeroBoleta" header="N° Boleta" sortable />
          <Column
            header="Pago"
            body={(row) =>
              formatDateCL(row?.fechaPago ?? row?.fechaEmision) || "-"
            }
            sortable
          />
          <Column
            header="Captura"
            body={fechaBody("fechaCaptura")}
            sortable
            sortField="fechaCaptura"
          />
          <Column field="cliente" header="Cliente" sortable />
          <Column
            header="Desde"
            body={fechaBody("periodoDesde")}
            sortable
            sortField="periodoDesde"
          />

          <Column
            header="Hasta"
            body={fechaBody("periodoHasta")}
            sortable
            sortField="periodoHasta"
          />

          <Column
            header="Monto total"
            body={montoTotalBoletaBody}
            sortable
            sortField="montoTotal"
            style={{ minWidth: 170 }}
          />

          <Column
            header="Medidores"
            body={medidoresBody}
            style={{ minWidth: 280 }}
          />

          <Column
            header="Recaudación Remarcadores"
            body={recaudacionBody}
            style={{ minWidth: 230 }}
            sortable
            sortField="recaudacionTeoricaTotal"
          />

          <Column
            header="Diferencia"
            body={diferenciaBody}
            style={{ minWidth: 260 }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default ConsumoMedidor;
